import {Checkbox, FlexRow, Link, SubHeading, Text} from '@forecast-it/design-system';
import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {LOCALSTORAGE_TIMESHEETS_SELECTED_TAB} from '../../../forecast-app/my-work-tab/my-timesheets-page/top-section/TimesheetsCalendarUtil';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../../forecast-app/shared/util/util';
import RevokeGoogleCalendarTokenMutation from '../../../mutations/revoke_google_calendar_token_mutation';
import DeleteOutlookCalendarTokensMutation from '../../../mutations/ts/integrations/calendars/DeleteOutlookCalendarTokensMutation';
import UpdateGoogleCalendarMutation from '../../../mutations/ts/integrations/calendars/UpdateGoogleCalendarMutation';
import UpdateOutlookCalendarMutation from '../../../mutations/ts/integrations/calendars/UpdateOutlookCalendarMutation';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {Calendar, CalendarIntegration, SettingsCalendarProps} from './CalendarTypes';
import GoogleCalendarOAuth from './GoogleCalendarOAuth';
import OutlookCalendarOAuth from './OutlookCalendarOAuth';

const CalendarSection = Styled.div`
	padding-top: 8px;
`;

const SubTitle = Styled.div`
	padding-right: 8px;
`;

const SubTitleSection = Styled.div`
	padding-top: 8px;
	display: flex;
	align-items: center;
`;

const Description = Styled.div`
	padding-top: 8px;
	padding-bottom: 16px;
	width: 35rem;
`;

const CheckboxContainer = Styled.div`
	display: flex;
	flex-direction: row;
	padding-top: 16px;
`;

const PrivacySettingsTitle = Styled.div`
	width 150px;
`;

const SettingsCheckbox = Styled.div`
	display: flex;
	flex-direction: column;
	> div {
		padding-bottom: 8px;
	}
`;

const ProfileContainer = Styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	> div {
		padding-right: 8px;
	}
`;

const SettingsCalendar = ({viewer, personId, type}: SettingsCalendarProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const calendarSettings: CalendarIntegration = useMemo(() => {
		return type === 'google' ? viewer.calendarIntegrations.googleCalendar : viewer.calendarIntegrations.outlookCalendar;
	}, [viewer.calendarIntegrations]);

	const [calendars, setCalendars] = useState<Calendar[]>(calendarSettings.calendars?.edges.map(edge => edge.node));
	const [enabledCalendars, setEnabledCalendars] = useState<string[]>(calendarSettings.enabledCalendars);

	if (enabledCalendars?.length !== calendarSettings.enabledCalendars?.length) {
		setEnabledCalendars(calendarSettings.enabledCalendars);
	}

	if (calendars?.length !== calendarSettings.calendars?.edges?.length) {
		setCalendars(calendarSettings.calendars?.edges.map(edge => edge.node));
	}

	const isCalendarEnabled = (id: string): boolean => {
		return enabledCalendars?.includes(id);
	};

	const updateEnabledCalendars = (calendarIds: string[]) => {
		trackEvent(`${type.charAt(0).toUpperCase() + type.slice(1)} Calendar Enabled Calendars`, 'Toggled');
		if (type === 'google') {
			Util.CommitMutation(UpdateGoogleCalendarMutation, {
				id: calendarSettings.id,
				enabledCalendarIds: calendarIds,
			});
		} else {
			Util.CommitMutation(UpdateOutlookCalendarMutation, {
				id: calendarSettings.id,
				enabledCalendarIds: calendarIds,
			});
		}
	};

	const toggleCalendar = (id: string): void => {
		let checkedCalendars: string[];
		if (isCalendarEnabled(id)) {
			checkedCalendars = enabledCalendars.filter(calendarId => calendarId !== id);
		} else {
			checkedCalendars = [id, ...enabledCalendars];
		}
		setEnabledCalendars(checkedCalendars);
		updateEnabledCalendars(checkedCalendars);
	};

	const toggleViewPrivate = () => {
		if (type === 'google') {
			trackEvent('Google Calendar View Private', 'Toggled', {viewPrivate: !calendarSettings.calendarViewPrivateEvents});
			Util.CommitMutation(UpdateGoogleCalendarMutation, {
				id: calendarSettings.id,
				viewPrivateEvents: !calendarSettings.calendarViewPrivateEvents,
			});
		} else {
			trackEvent('Outlook Calendar View Private', 'Toggled', {viewPrivate: !calendarSettings.calendarViewPrivateEvents});
			Util.CommitMutation(UpdateOutlookCalendarMutation, {
				id: calendarSettings.id,
				viewPrivateEvents: !calendarSettings.calendarViewPrivateEvents,
			});
		}
	};

	const revokeTokens = () => {
		const callbackPositive = () => {
			if (type === 'google') {
				trackEvent('Google Calendar Unlink Account', 'Clicked');
				localStorage.removeItem(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB);
				Util.CommitMutation(
					RevokeGoogleCalendarTokenMutation,
					{
						personId: personId,
					},
					() => {
						setCalendars([]);
						setEnabledCalendars([]);
					}
				);
			} else if (type === 'outlook') {
				trackEvent('Outlook Calendar Unlink Account', 'Clicked');
				localStorage.removeItem(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB);
				Util.CommitMutation(
					DeleteOutlookCalendarTokensMutation,
					{
						id: calendarSettings.id,
					},
					() => {
						setCalendars([]);
						setEnabledCalendars([]);
					}
				);
			}
		};

		showModal(
			{
				type: MODAL_TYPE.CONFIRMATION,
				title: formatMessage({id: 'calendar_integration.confirm_unlink_account'}),
				description: formatMessage({id: 'calendar_integration.confirm_unlink_account_description'}),
				callbackPositive,
			},
			true
		);
	};

	return (
		<CalendarSection>
			<SubTitleSection>
				<SubTitle>
					<SubHeading size={'1'}>{`View your ${
						type.charAt(0).toUpperCase() + type.slice(1)
					} Calendar events inside Forecast`}</SubHeading>
				</SubTitle>
			</SubTitleSection>
			<Description>
				<Text size={'2'}>
					The integration fetches your {type.charAt(0).toUpperCase() + type.slice(1)} calendar events into Forecast,
					making it easy to reference what you worked on when you need to log time. That means no more jumping back
					and forth between your {type.charAt(0).toUpperCase() + type.slice(1)} calendar and Forecast.
				</Text>
			</Description>

			<FlexRow>
				{calendarSettings.calendarUser ? (
					<ProfileContainer>
						<div>Linked {type.charAt(0).toUpperCase() + type.slice(1)} Account:</div>
						{calendarSettings.calendarUser.picture ? (
							<div>
								<img
									crossOrigin="anonymous"
									src={calendarSettings.calendarUser.picture}
									width="32"
									height="32"
									alt={`${type} profile`}
								/>
							</div>
						) : null}
						<div>{calendarSettings.calendarUser.name}</div>
						<Link onClick={revokeTokens}>Unlink Account</Link>
					</ProfileContainer>
				) : null}
				{!calendarSettings.calendarUser || calendarSettings.calendarTokenError ? (
					type === 'google' ? (
						<GoogleCalendarOAuth calendarTokenError={calendarSettings.calendarTokenError} personId={personId} />
					) : (
						<OutlookCalendarOAuth calendarTokenError={calendarSettings.calendarTokenError} page="my-profile" />
					)
				) : null}
			</FlexRow>

			{calendars?.length ? (
				<>
					<CheckboxContainer>
						<PrivacySettingsTitle>Shown Calendars:</PrivacySettingsTitle>
						<SettingsCheckbox>
							{calendars.map(calendar => {
								return (
									<Checkbox
										checked={isCalendarEnabled(calendar.id)}
										onChange={() => toggleCalendar(calendar.id)}
										labelText={calendar.name}
										notBoldLabel={true}
										bgColor={calendar.backgroundColor}
										fgColor={calendar.foregroundColor}
									/>
								);
							})}
						</SettingsCheckbox>
					</CheckboxContainer>
					<CheckboxContainer>
						<PrivacySettingsTitle>Privacy settings:</PrivacySettingsTitle>
						<SettingsCheckbox>
							<Checkbox
								checked={calendarSettings.calendarViewPrivateEvents}
								onChange={toggleViewPrivate}
								labelText={`View ${
									type.charAt(0).toUpperCase() + type.slice(1)
								} Calendar events that are marked as private`}
								notBoldLabel={true}
							/>
						</SettingsCheckbox>
					</CheckboxContainer>
				</>
			) : null}
		</CalendarSection>
	);
};

export default SettingsCalendar;
